import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';
import { PenTool, Layout, Cpu, Smartphone, MessageSquare, BrainCircuit } from 'lucide-react';

const icons = [Cpu, Layout, BrainCircuit, PenTool, Smartphone, MessageSquare];

export default function ServicesSection() {
  const { lang } = useLanguage();
  const t = translations.services[lang];

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Navy diagonal background */}
      <div className="absolute inset-0 bg-enbi-navy" style={{ clipPath: 'polygon(0 3%, 100% 0, 100% 97%, 0 100%)' }} />

      {/* Gold diagonal accent */}
      <div
        className="absolute top-0 left-0 w-[40%] h-full bg-enbi-gold/[0.06]"
        style={{ clipPath: 'polygon(0 0, 60% 0, 30% 100%, 0 100%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-[2px] bg-enbi-gold" />
            <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">{t.label}</span>
            <div className="w-10 h-[2px] bg-enbi-gold" />
          </div>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white uppercase tracking-tight mb-4">
            {t.headline}
          </h2>
          <p className="text-white/60 text-base max-w-2xl mx-auto">{t.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`service-card-${i}`}
                className="group relative bg-white/[0.05] border border-white/10 p-8 hover:bg-white/[0.08] transition-all duration-500 hover:border-enbi-gold/30"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%)' }}
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6 border border-enbi-gold/40">
                  <Icon className="text-enbi-gold" size={22} strokeWidth={1.25} />
                </div>
                <h3 className="font-outfit text-white text-sm tracking-[0.15em] uppercase font-semibold mb-4">
                  {item.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">
                  {item.desc}
                </p>
                {/* Gold accent line at bottom */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-enbi-gold group-hover:w-full transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
